import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import Button from '../../components/ui/Button';
import SearchBar from './components/SearchBar';
import TalentTable from './components/TalentTable';

import EmptyState from './components/EmptyState';
import { getTalentProfiles } from '../../services/talentService';
import Icon from '../../components/AppIcon';

const TalentManagementDashboard = () => {
  const navigate = useNavigate();
  const { user, userProfile, isSuperUser, signOut } = useAuth();
  const [talents, setTalents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');

  // Redirect if not superuser
  useEffect(() => {
    if (user && !isSuperUser()) {
      navigate('/song-database-list');
    }
  }, [user, isSuperUser, navigate]);

  // Load talent profiles with video counts
  const loadTalentProfiles = async () => {
    setLoading(true);
    setError(null);

    try {
      const { data, error: serviceError } = await getTalentProfiles(searchTerm, sortBy, sortOrder);
      
      if (serviceError) {
        // Convert error object to string for proper React rendering
        const errorMessage = typeof serviceError === 'object' 
          ? serviceError?.message || serviceError?.details || JSON.stringify(serviceError)
          : serviceError;
        setError(errorMessage);
        setTalents([]);
      } else {
        setTalents(data || []);
      }
    } catch (err) {
      console.error('Error loading talent profiles:', err);
      // Convert caught error to string for proper React rendering
      const errorMessage = err?.message || 'Failed to load talent profiles. Please try again.';
      setError(errorMessage);
      setTalents([]);
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    if (user && isSuperUser()) {
      loadTalentProfiles();
    }
  }, [user, isSuperUser, searchTerm, sortBy, sortOrder]);

  const handleAddTalent = () => {
    navigate('/add-edit-talent-profile');
  };

  const handleEditTalent = (talentId) => {
    navigate(`/add-edit-talent-profile/${talentId}`);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  const handleSort = (field, order) => {
    setSortBy(field);
    setSortOrder(order);
  };

  const handleRefresh = () => {
    loadTalentProfiles();
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/');
  };

  // Calculate stats with unassigned talent (those without videos)
  const stats = {
    totalTalent: talents?.length || 0,
    unassignedTalent: talents?.filter(talent => !talent?.video_count || talent?.video_count === 0)?.length || 0,
    recentAdditions: talents?.filter(talent => {
      const createdDate = new Date(talent?.created_at);
      const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
      return createdDate > weekAgo;
    })?.length || 0
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-blue-900 to-purple-900 flex items-center justify-center">
        <LoadingSpinner size="lg" className="text-white" />
      </div>
    );
  }

  if (!user || !isSuperUser()) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-blue-900 to-purple-900">
        <Header 
          title="Access Denied" 
          user={userProfile}
          onLogout={handleLogout}
        />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-white mb-2">Access Denied</h2>
            <p className="text-blue-200">You need SuperUser privileges to access this page.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-blue-900 to-purple-900">
      <Header 
        title="All Talent Dashboard" 
        user={userProfile}
        onLogout={handleLogout}
        actions={[
          <Button 
            key="songs"
            variant="outline" 
            onClick={() => navigate('/song-database-list')}
            className="border-white text-white hover:bg-white hover:text-indigo-900 rounded-lg"
          >
            Song List
          </Button>
        ]}
      />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white">All Talent</h1>
            <p className="text-blue-100 mt-2">
              View and Edit the Video Talent
            </p>
          </div>
          
          <div className="flex items-center space-x-3">
            <Button
              variant="outline"
              onClick={handleRefresh}
              disabled={loading}
              className="flex items-center space-x-2 border-white text-white hover:bg-white hover:text-indigo-900 rounded-lg"
            >
              <Icon name="RefreshCw" size={18} />
              <span>Refresh</span>
            </Button>
            
            <Button
              onClick={handleAddTalent}
              className="flex items-center space-x-2 bg-white text-indigo-900 hover:bg-gray-100 rounded-lg"
            >
              <Icon name="Plus" size={18} />
              <span>Add New Talent</span>
            </Button>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-xl shadow-lg overflow-hidden">
            <div className="p-5">
              <div className="flex flex-col items-center text-center">
                <div className="flex-shrink-0 mb-2">
                  <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center mx-auto">
                    <span className="text-white text-sm font-bold">
                      {stats?.totalTalent}
                    </span>
                  </div>
                </div>
                <div className="w-full">
                  <dl>
                    <dt className="text-sm font-medium text-blue-100 truncate">Total Talent</dt>
                    <dd className="text-lg font-medium text-white">Registered performers</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-xl shadow-lg overflow-hidden">
            <div className="p-5">
              <div className="flex flex-col items-center text-center">
                <div className="flex-shrink-0 mb-2">
                  <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center mx-auto">
                    <span className="text-white text-sm font-bold">
                      {stats?.unassignedTalent}
                    </span>
                  </div>
                </div>
                <div className="w-full">
                  <dl>
                    <dt className="text-sm font-medium text-blue-100 truncate">Unassigned Talent</dt>
                    <dd className="text-lg font-medium text-white">Without video assignments</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-xl shadow-lg overflow-hidden">
            <div className="p-5">
              <div className="flex flex-col items-center text-center">
                <div className="flex-shrink-0 mb-2">
                  <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center mx-auto">
                    <span className="text-white text-sm font-bold">
                      {stats?.recentAdditions}
                    </span>
                  </div>
                </div>
                <div className="w-full">
                  <dl>
                    <dt className="text-sm font-medium text-blue-100 truncate">Recent Additions</dt>
                    <dd className="text-lg font-medium text-white">Added this week</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6 mb-8">
          <SearchBar
            searchTerm={searchTerm}
            onSearch={handleSearch}
            onSort={handleSort}
            sortBy={sortBy}
            sortOrder={sortOrder}
          />
        </div>

        {/* Error State */}
        {error && (
          <div className="bg-red-50 bg-opacity-90 border border-red-200 rounded-xl p-6 mb-8">
            <div className="flex items-center space-x-3">
              <Icon name="AlertCircle" className="text-red-800" size={20} />
              <div>
                <h3 className="font-medium text-red-800">Error</h3>
                <p className="text-red-700 text-sm mt-1">
                  {typeof error === 'string' ? error : 'An unexpected error occurred'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Content */}
        {!loading && !error && (
          <>
            {talents?.length > 0 ? (
              <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl overflow-hidden">
                <TalentTable
                  talents={talents}
                  onEdit={handleEditTalent}
                  onRefresh={handleRefresh}
                />
              </div>
            ) : (
              <EmptyState onAddTalent={handleAddTalent} />
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default TalentManagementDashboard;